import { useState, useRef } from "react";
import { useAdminExpenses, useAdminCreateExpense, useAdminUpdateExpense, useAdminMe } from "../hooks/useApi";
import { formatNumber } from "../utils/format";
import "../admin-theme.css";

export default function AdminExpenses() {
  const { data: expenses, isLoading } = useAdminExpenses();
  const { data: me } = useAdminMe();
  const createExpense = useAdminCreateExpense();
  const updateExpense = useAdminUpdateExpense();

  const canManage = Boolean(me?.is_admin) || Boolean(me?.permissions?.includes("expenses.manage"));

  const [showModal, setShowModal] = useState(false);
  const overlayMouseDown = useRef(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const total = (expenses || []).reduce(
    (sum: number, e: { amount: number }) => sum + Number(e.amount || 0),
    0
  );

  const openCreate = () => {
    setEditingId(null);
    setAmount("");
    setDescription("");
    setShowModal(true);
  };

  const openEdit = (expense: Record<string, unknown>) => {
    setEditingId(expense.expense_id as number);
    const raw = Number(expense.amount);
    setAmount(Number.isFinite(raw) ? String(Math.trunc(raw)) : "");
    setDescription((expense.description as string) || "");
    setShowModal(true);
  };

  const handleSubmit = () => {
    const numAmount = Number(amount.replace(/[^0-9.]/g, ""));
    if (!numAmount || !description.trim()) return;
    const payload = { amount: numAmount, description: description.trim() };
    const onSuccess = () => {
      setShowModal(false);
      setEditingId(null);
      setAmount("");
      setDescription("");
    };
    if (editingId) {
      updateExpense.mutate({ expenseId: editingId, data: payload }, { onSuccess });
    } else {
      createExpense.mutate(payload, { onSuccess });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>هزینه‌ها</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>ثبت و مشاهده هزینه‌های دستی سیستم</p>
        </div>
        {canManage && (
          <button
            className="admin-btn admin-btn-primary"
            onClick={openCreate}
          >
            + ثبت هزینه
          </button>
        )}
      </div>

      {/* Total Card */}
      <div className="admin-metric-card">
        <div className="admin-metric-card-accent admin-metric-card-accent-dark" />
        <div className="admin-metric-card-content">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: "var(--admin-text-tertiary)" }}>مجموع هزینه‌ها</span>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(7, 135, 131, 0.08)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#078783" strokeWidth="1.5" strokeLinecap="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
              </svg>
            </div>
          </div>
          <span className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>
            {formatNumber(total)}
            <span className="text-sm font-normal mr-2" style={{ color: "var(--admin-text-tertiary)" }}>تومان</span>
          </span>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">لیست هزینه‌ها</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !expenses?.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
            <p className="text-sm">هیچ هزینه‌ای ثبت نشده است</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>مبلغ (تومان)</th>
                  <th>توضیحات</th>
                  <th>ثبت‌کننده</th>
                  <th>تاریخ ثبت</th>
                  {canManage && <th></th>}
                </tr>
              </thead>
              <tbody>
                {(expenses as Record<string, unknown>[]).map((expense) => (
                  <tr key={expense.expense_id as string}>
                    <td>
                      <span className="font-number text-sm" style={{ color: "var(--admin-text-primary)" }}>
                        {formatNumber(expense.amount as number)}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>
                        {expense.description as string}
                      </span>
                    </td>
                    <td>
                      <div className="text-sm" style={{ color: "var(--admin-text-primary)" }}>
                        {expense.creator_name as string || "—"}
                      </div>
                      <div className="text-xs ltr text-left" style={{ color: "var(--admin-text-tertiary)" }}>
                        {expense.creator_email as string}
                      </div>
                    </td>
                    <td>
                      <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                        {new Date(expense.created_at as string).toLocaleDateString("fa-IR")}
                      </span>
                    </td>
                    {canManage && (
                      <td className="text-left">
                        <button
                          onClick={() => openEdit(expense)}
                          className="p-1.5 rounded-lg transition-colors hover:opacity-70"
                          style={{ color: "var(--admin-primary)" }}
                          title="ویرایش هزینه"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showModal && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setShowModal(false); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
<h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                  {editingId ? "ویرایش هزینه" : "ثبت هزینه جدید"}
                </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  مبلغ (تومان)
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="admin-input"
                  dir="ltr"
                  placeholder="مثلاً 250000"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  توضیحات
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="admin-input"
                  rows={3}
                  placeholder="شرح هزینه"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={(createExpense.isPending || updateExpense.isPending) || !Number(amount) || !description.trim()}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  {(createExpense.isPending || updateExpense.isPending) ? "در حال ثبت..." : editingId ? "ویرایش هزینه" : "ثبت هزینه"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
